// Pure helpers for FindInGrid (Ctrl+F). No MUI imports, so this file is
// unit-testable as is.

export type FindRowId = string | number;

export interface FindColumn {
  field: string;
  /** Index among the grid's visible columns, for apiRef.scrollToIndexes. */
  colIndex: number;
}

export interface FindMatch {
  id: FindRowId;
  field: string;
  /** Index among the rows on the current page, for apiRef.scrollToIndexes. */
  rowIndex: number;
  colIndex: number;
}

/**
 * What a cell shows as text, from the grid's cell params. The formatted value
 * wins over the raw one so dates and prices match the way they're displayed.
 * Objects (columns rendered from structured values) have no text to search.
 */
export function cellSearchText(
  params: { formattedValue?: unknown; value?: unknown } | null | undefined
): string {
  if (!params) return "";
  const value = params.formattedValue ?? params.value;
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toLocaleString();
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  return "";
}

/**
 * Cells containing `query` (case-insensitive), in reading order: row by row,
 * left to right. `getText` is called once per cell, so pass the rows on the
 * current page and the visible columns only.
 */
export function findMatches(
  rowIds: ReadonlyArray<FindRowId>,
  columns: ReadonlyArray<FindColumn>,
  getText: (id: FindRowId, field: string) => string,
  query: string
): FindMatch[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  const matches: FindMatch[] = [];
  rowIds.forEach((id, rowIndex) => {
    for (const { field, colIndex } of columns) {
      if (getText(id, field).toLowerCase().includes(needle)) {
        matches.push({ id, field, rowIndex, colIndex });
      }
    }
  });
  return matches;
}

/** The next (or previous) match index, wrapping around; -1 with no matches. */
export function stepMatch(
  current: number,
  count: number,
  direction: 1 | -1
): number {
  if (count <= 0) return -1;
  if (current < 0) return direction === 1 ? 0 : count - 1;
  return (current + direction + count) % count;
}
